import { useAuth } from "@dl/web-core-js/auth";
import { Button, Dialog, Field, Input, MultiSelect, Select } from "@dl/web-core-js/ui";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { ProductInventoryRuleClient, SalesChannelClient, type SalesChannelDTO } from "../../api/inventory";
import { INVENTORY_RULE_MODES, type InventoryRuleMode } from "../../api/product";
import { modeLabel } from "./InventoryRulesTab";

const KEEP = "KEEP";

/**
 * One change to the inventory rules of the products selected in the grid, on
 * the channels picked here, in one request.
 *
 * Each field can keep what the rules have: the mode (with a floor when FLOOR),
 * the listing — "not listed" is how a product leaves a channel's file; BLOCKED
 * sends an explicit 0, which is how it comes down on a channel that keeps the
 * last number it was told — and the lock, on by default so the daily rule job
 * leaves a deliberate change alone.
 *
 * The server applies the hard rules per product on top (components BLOCKED and
 * unlisted, DISCONTINUED / PENDING_LAUNCH BLOCKED, no FLOOR while
 * PENDING_DISCONTINUED, a bundle's Dispatchable or Floor becomes "From its
 * items"), and the product's dispatch list can still only lower
 * the published number; the hint says so rather than refusing.
 */
export function InventoryRuleBulkDialog({
  open,
  productIds,
  onClose,
  onDone,
}: {
  open: boolean;
  /** The grid's selection. */
  productIds: number[];
  onClose: () => void;
  /** After a successful write, so the grid can reload. */
  onDone: () => void;
}) {
  const { t } = useTranslation();
  const { http } = useAuth();
  const channelClient = useMemo(() => new SalesChannelClient(http), [http]);
  const ruleClient = useMemo(() => new ProductInventoryRuleClient(http), [http]);

  const channels = useQuery({
    queryKey: ["sales-channel", "all"],
    queryFn: () => channelClient.findAll(),
    staleTime: 30 * 60_000,
    enabled: open,
  });
  const all = useMemo(
    () => [...(channels.data ?? [])].sort((a, b) => a.sort_order - b.sort_order || a.name.localeCompare(b.name)),
    [channels.data],
  );

  const [picked, setPicked] = useState<string[]>([]);
  const [mode, setMode] = useState<string>(KEEP);
  const [floor, setFloor] = useState("10");
  const [listed, setListed] = useState<string>(KEEP);
  const [locked, setLocked] = useState(true);

  // A fresh form every time: a channel list left over from the last operation,
  // applied to a new selection, is exactly the mistake to prevent.
  useEffect(() => {
    if (open) {
      setPicked([]);
      setMode(KEEP);
      setFloor("10");
      setListed(KEEP);
      setLocked(true);
    }
  }, [open]);

  const options = useMemo(
    () =>
      all.map((channel) => ({
        value: String(channel.id),
        label: channel.market ? `${channel.name} · ${channel.market}` : channel.name,
      })),
    [all],
  );
  const pickMarket = (market: SalesChannelDTO["market"]) =>
    setPicked(all.filter((channel) => market === undefined || channel.market === market).map((channel) => String(channel.id)));

  const floorValue = Number(floor);
  const floorInvalid = mode === "FLOOR" && (!Number.isFinite(floorValue) || floorValue < 1);
  const rules = productIds.length * picked.length;

  const apply = useMutation({
    mutationFn: () =>
      ruleClient.bulk(productIds, picked.map(Number), {
        mode: mode === KEEP ? null : (mode as InventoryRuleMode),
        floor_quantity: mode === "FLOOR" ? floorValue : null,
        is_listed: listed === KEEP ? null : listed === "yes",
        is_locked: locked,
      }),
    onSuccess: (changed) => {
      toast.success(t("products.rules.applied", "Rules set on {{count}} products", { count: changed.length }));
      onDone();
      onClose();
    },
    onError: (failure) =>
      toast.error(failure instanceof Error ? failure.message : t("error.generic", "Request failed")),
  });

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={t("products.rules.title", "Set inventory rules on {{count}} products", { count: productIds.length })}
      description={t(
        "products.rules.description",
        "Every selected product gets this change on every channel picked. A field left on \"Keep as is\" keeps what each rule has.",
      )}
      size="md"
      actions={
        <>
          <Button variant="secondary" onClick={onClose} disabled={apply.isPending}>
            {t("button.cancel", "Cancel")}
          </Button>
          <Button
            variant="primary"
            onClick={() => apply.mutate()}
            disabled={picked.length === 0 || floorInvalid}
            loading={apply.isPending}
          >
            {t("button.apply", "Apply")}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <Field label={t("product.rules.platform", "Channel")} htmlFor="bulk-rule-channels">
          <MultiSelect
            options={options}
            value={picked}
            onChange={setPicked}
            placeholder={t("products.rules.pick-channels", "Pick channels")}
            searchThreshold={8}
          />
          <div className="mt-1.5 flex flex-wrap gap-1.5">
            <Button size="sm" variant="ghost" onClick={() => pickMarket(undefined)}>
              {t("products.rules.pick.all", "All channels")}
            </Button>
            <Button size="sm" variant="ghost" onClick={() => pickMarket("CA")}>
              {t("products.rules.pick.ca", "Canada")}
            </Button>
            <Button size="sm" variant="ghost" onClick={() => pickMarket("US")}>
              {t("products.rules.pick.us", "United States")}
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setPicked([])} disabled={picked.length === 0}>
              {t("products.rules.pick.clear", "Clear")}
            </Button>
          </div>
        </Field>

        <div className="grid gap-4 sm:grid-cols-2">
          <Field label={t("product.rules.mode", "Rule")} htmlFor="bulk-rule-mode">
            <Select
              id="bulk-rule-mode"
              value={mode}
              options={[
                { value: KEEP, label: t("product.rules.keep", "Keep as is") },
                ...INVENTORY_RULE_MODES.map((entry) => ({ value: entry, label: modeLabel(t, entry) })),
              ]}
              onChange={(event) => setMode(event.target.value)}
            />
          </Field>
          <Field label={t("product.rules.listed", "Listed")} htmlFor="bulk-rule-listed">
            <Select
              id="bulk-rule-listed"
              value={listed}
              options={[
                { value: KEEP, label: t("product.rules.keep", "Keep as is") },
                { value: "yes", label: t("product.rules.listed.yes", "Listed") },
                { value: "no", label: t("product.rules.listed.no", "Not listed") },
              ]}
              onChange={(event) => setListed(event.target.value)}
            />
          </Field>
          {mode === "FLOOR" && (
            <Field
              label={t("product.rules.floor", "Floor quantity")}
              htmlFor="bulk-rule-floor"
              required
              error={floorInvalid ? t("product.rules.floor.min", "At least 1") : undefined}
            >
              <Input
                id="bulk-rule-floor"
                type="number"
                min={1}
                value={floor}
                onChange={(event) => setFloor(event.target.value)}
              />
            </Field>
          )}
        </div>

        <label className="inline-flex items-center gap-2 text-[13px]">
          <input
            type="checkbox"
            checked={locked}
            onChange={(event) => setLocked(event.target.checked)}
            className="size-3.5 accent-[var(--accent)]"
          />
          {t("product.rules.lock-these", "Lock these rules (the daily job leaves them alone)")}
        </label>

        <p className="text-[12px] text-ink-muted">
          {picked.length === 0
            ? t("products.rules.pick-first", "Pick at least one channel.")
            : t("products.rules.summary", "{{rules}} rules: {{products}} products × {{channels}} channels.", {
                rules,
                products: productIds.length,
                channels: picked.length,
              })}{" "}
          {t(
            "products.rules.hint",
            "Components, discontinued, not-yet-launched and pending-discontinued products keep their hard rules whatever is set here. On a bundle, Dispatchable or Floor becomes \"From its items\"; only Blocked applies as is.",
          )}
        </p>
      </div>
    </Dialog>
  );
}
